import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import "flatpickr/dist/flatpickr.min.css"
import { French } from "flatpickr/dist/l10n/fr.js"
import { applyZIndexAboveOpenModals } from "../src/shared/helpers/modal_stacking"
import faCalendarIcon from "../images/icons/fa_calendar_week.svg"
import phCalendarIcon from "../images/icons/ph_calendar_blank.svg"

const INPUT_SMOOTH_FILLED_CLASS = "input--filled"

// The admin keeps Font Awesome (`data-icon-set` on <html>); other layouts use Phosphor.
const calendarIcon = () => (document.documentElement.dataset.iconSet === "fa" ? faCalendarIcon : phCalendarIcon)

export default class extends Controller {
  static values = {
    config: Object,
    initialViewDate: String,
    submitOnCompleteRange: { type: Boolean, default: false }
  }

  connect () {
    if (this.element._flatpickr) {
      this.flatpickr = this.element._flatpickr
      return
    }

    const modalElement = this.element.closest(".modal")
    if (modalElement && !modalElement.classList.contains("show")) {
      this.modalElement = modalElement
      this.onModalShown = () => this.initializeFlatpickr()
      this.modalElement.addEventListener("shown.bs.modal", this.onModalShown)
      return
    }

    this.initializeFlatpickr()
  }

  disconnect () {
    if (this.modalElement && this.onModalShown) {
      this.modalElement.removeEventListener("shown.bs.modal", this.onModalShown)
    }

    if (this.dropdownToggle && this.onDropdownHide) {
      this.dropdownToggle.removeEventListener("hide.bs.dropdown", this.onDropdownHide)
    }

    const instance = this.picker || this.flatpickr
    instance?.destroy()
    this.picker = null
    this.flatpickr = null
  }

  initializeFlatpickr () {
    if (this.element._flatpickr) {
      this.flatpickr = this.element._flatpickr
      return
    }

    const defaultConfig = {
      altInput: true,
      dateFormat: "Y-m-d H:i",
      locale: French
    }

    const config = { ...defaultConfig, ...this.configValue }
    this.submitFormWhenRangeIsComplete(config)
    const isInputSmooth = this.element.dataset.isInputSmooth !== undefined
    const initialViewDate = this.hasInitialViewDateValue ? this.initialViewDateValue : null
    const raiseAboveModals = Boolean(this.element.closest(".modal"))

    // Calendar is portaled to body at z-index 1060. Only raise it when the
    // input lives in a modal (nested create/manage stacks above that).
    if (raiseAboveModals || initialViewDate) {
      const userOnOpen = config.onOpen

      config.onOpen = (...args) => {
        const [, dateStr, instance] = args

        if (raiseAboveModals) applyZIndexAboveOpenModals(instance.calendarContainer)
        if (initialViewDate && !dateStr) instance.jumpToDate(initialViewDate)

        userOnOpen?.(...args)
      }
    }

    if (isInputSmooth) {
      const userOnReady = config.onReady

      config.onReady = (...args) => {
        this.setupInputSmooth(args[2])
        userOnReady?.(...args)
      }
    }

    const userOnReady = config.onReady
    config.onReady = (...args) => {
      this.applyCalendarIcon(args[2]?.altInput || args[2]?.input || this.element)
      userOnReady?.(...args)
    }

    this.picker = flatpickr(this.element, config)
    this.keepDropdownOpenWhileCalendarIsOpen()

    document.querySelectorAll(".flatpickr").forEach((input) => {
      if (input.type === "hidden") return

      this.applyCalendarIcon(input)
    })
  }

  keepDropdownOpenWhileCalendarIsOpen () {
    const dropdown = this.element.closest(".dropdown")
    this.dropdownToggle = dropdown?.querySelector("[data-bs-toggle='dropdown']")
    if (!this.dropdownToggle) return

    this.onDropdownHide = (event) => {
      const clickTarget = event.clickEvent?.target
      const clickedCalendar = Boolean(clickTarget?.closest?.(".flatpickr-calendar"))

      if (this.picker?.isOpen || clickedCalendar) event.preventDefault()
    }

    this.dropdownToggle.addEventListener("hide.bs.dropdown", this.onDropdownHide)
  }

  submitFormWhenRangeIsComplete (config) {
    if (!this.submitOnCompleteRangeValue) return

    const userOnChange = [].concat(config.onChange || [])

    config.onChange = (...args) => {
      userOnChange.forEach((hook) => hook?.(...args))

      if (args[0].length === 2) this.element.closest("form")?.requestSubmit()
    }
  }

  setupInputSmooth (instance) {
    const { altInput, input } = instance

    if (!altInput) return

    input.classList.remove("input--smooth")
    input.style.backgroundImage = "none"
    input.style.paddingRight = ""

    this.applyCalendarIcon(altInput)

    const syncFilled = () => {
      const hasValue = input.value.trim() !== ""
      const isFocused = document.activeElement === altInput

      altInput.classList.toggle(INPUT_SMOOTH_FILLED_CLASS, hasValue || isFocused)
    }

    syncFilled()

    altInput.addEventListener("focus", syncFilled)
    altInput.addEventListener("blur", syncFilled)

    const userOnChange = [].concat(instance.config.onChange || [])
    instance.set("onChange", [...userOnChange, syncFilled])
  }

  applyCalendarIcon (input) {
    input.style.backgroundImage = `url(${calendarIcon()})`
    input.style.backgroundRepeat = "no-repeat"
    input.style.backgroundPosition = "right 10px center"
    input.style.backgroundSize = "14px"
    input.style.paddingRight = "25px"
  }
}
